import React, { useState, useEffect } from "react";
import styles from "./ad_styles";
import LoginForm from "./LoginForm";
import {eventstyles} from "./styles";
import NotificationContainer from './notification';

// Simple API helper with token support
const apiCall = async (url, method = "GET", data = null, token, logout) => {
  const options = {
    method,
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
  if (data) options.body = JSON.stringify(data);

  const response = await fetch(url, options);
  if (!response.ok && response.status === 401) {
    logout();
    return;
  }
  return response;
};

function Modal({ title, children, onClose }) {
  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>{title}</h3>
          <button 
            style={{
              ...styles.dangerButton,
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              padding: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }} 
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div style={styles.modalBody}>{children}</div>
      </div>
    </div>
  );
}

function Navbar({ setPage, logout, currentPage }) {
  const pages = [
    ["Dashboard", "dashboard", "📊"],
    ["Articles", "articles", "📝"],
    ["Events", "events", "🎯"],
    ["Members", "members", "👥"],
    ["Contacts", "contacts", "💬"],
  ];

  return (
    <div style={styles.navbar}>
      <div style={styles.navButtons}>
        {pages.map(([label, page, icon]) => (
          <button 
            key={page} 
            onClick={() => setPage(page)} 
            style={{
              ...styles.navButton,
              ...(currentPage === page ? styles.navButtonActive : {})
            }}
            onMouseEnter={(e) => {
              if (currentPage !== page) {
                Object.assign(e.target.style, styles.navButtonHover);
              }
            }}
            onMouseLeave={(e) => {
              if (currentPage !== page) {
                e.target.style.background = 'transparent';
                e.target.style.color = '#64748b';
                e.target.style.transform = 'none';
              }
            }}
          >
            <span style={{ marginRight: '8px' }}>{icon}</span>
            {label}
          </button>
        ))}
      </div>
      <button 
        onClick={logout} 
        style={styles.logoutButton}
        onMouseEnter={(e) => {
          e.target.style.transform = 'translateY(-1px)';
          e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'none';
          e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.2)';
        }}
      >
        🚪 Logout
      </button>
    </div>
  );
}

function Dashboard({ token, logout, showNotification }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await apiCall("/api/admin/stats", "GET", null, token, logout);
        if (response.ok) {
            const data = await response.json();
            setStats(data);
        } else {
            showNotification('unable to load stats', 'error')
        }
      } catch (err) {
        console.error(err);
        showNotification('unable to load stats', 'error')
      }
    })();
  }, [token]);

  if (!stats) {
    return (
      <div style={styles.emptyState}>
        <div style={styles.loadingSpinner}></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  const statCards = [
    { label: "Total Members", value: stats.total_members, icon: "👥", color: "#0f5132" },
    { label: "Published Articles", value: stats.total_articles, icon: "📝", color: "#1e40af" },
    { label: "Total Events", value: stats.total_events, icon: "🎯", color: "#7c3aed" },
    { label: "Active Events", value: stats.active_events || 0, icon: "🔥", color: "#dc2626" },
    { label: "Pending Contacts", value: stats.messages_breakdown.new || 0, icon: "💬", color: "#ea580c" }
  ];

  return (
    <div>
      <h2 style={styles.cardTitle}>
        <span>📊</span>
        Dashboard Overview
      </h2>
      <div style={styles.statsGrid}>
        {statCards.map((stat, index) => (
          <div 
            key={index} 
            style={styles.statCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)';
            }}
          >
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>{stat.icon}</div>
            <div style={styles.statNumber}>{stat.value}</div>
            <div style={styles.statLabel}>{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Events({ token, logout, showNotification }) {
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [current, setCurrent] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [showRsvpModal, setShowRsvpModal] = useState(false);
  
  // Mock data for demonstration
  const fetchEvents = async () => {
    const response = await apiCall("/api/admin/events", "GET", null, token, logout);
    if (response.ok) {
        const data = await response.json()
        setEvents(data || []);
    } else {
        showNotification('cannot load events', 'error');
    }
  };

  useEffect(() => { fetchEvents(); }, [token]);

  const handleSave = async (event) => {
    let response = undefined;
    if (event.id) {
      response = await apiCall(`/api/admin/events/${event.id}`, "PUT", event, token, logout);
    } else {
      response = await apiCall("/api/admin/events", "POST", event, token, logout);
    }
    if (response.ok) {
        setShowModal(false);
        fetchEvents();
        showNotification(event.id ? 'Successful updated new event': 'Successful added new event', 'success');
    } else {
        showNotification('Error adding events', 'error');
    }
  };

  const fetchRsvps = async (eventId) => {
    const response = await apiCall(`/api/admin/events/${eventId}/rsvps`, "GET", null, token, logout);
    if (response.ok) {
        const data = await response.json()
        setRsvps(data || []);
        setShowRsvpModal(true);
    } else {
        showNotification('Error adding events', 'error');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={styles.cardTitle}>
          <span>🎯</span>
          Events Management
        </h2>
        <button 
          style={styles.primaryButton} 
          onClick={() => { setCurrent(null); setShowModal(true); }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 6px 20px rgba(15, 81, 50, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'none';
            e.target.style.boxShadow = '0 4px 12px rgba(15, 81, 50, 0.2)';
          }}
        >
          <span>➕</span>
          Add Event
        </button>
      </div>

      <table style={styles.table}>
        <thead style={styles.tableHeader}>
          <tr>
            <th style={styles.tableHeaderCell}>Event Details</th>
            <th style={styles.tableHeaderCell}>Date & Location</th>
            <th style={styles.tableHeaderCell}>Type</th>
            <th style={styles.tableHeaderCell}>Price</th>
            <th style={styles.tableHeaderCell}>RSVPs</th>
            <th style={styles.tableHeaderCell}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr 
              key={e.id} 
              style={styles.tableRow}
              onMouseEnter={(el) => {
                el.currentTarget.style.background = styles.tableRowHover.background;
              }}
              onMouseLeave={(el) => {
                el.currentTarget.style.background = 'white';
              }}
            >
              <td style={styles.tableCell}>
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>{e.title}</div>
                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                  {e.description}
                </div>
              </td>
              <td style={styles.tableCell}>
                <div style={{ marginBottom: '4px' }}>📅 {e.date}</div>
                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                  📍 {e.location}
                </div>
              </td>
              <td style={styles.tableCell}>
                <span style={{
                  ...styles.badge,
                  ...styles.badgeInfo
                }}>
                  {e.event_type}
                </span>
              </td>
              <td style={styles.tableCell}>
                <span style={{ fontWeight: '600', color: '#0f5132' }}>
                  {e.price}
                </span>
              </td>
              <td style={styles.tableCell}>
                <button
                  style={{
                    ...styles.badge,
                    ...styles.badgeSuccess,
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  onClick={() => fetchRsvps(e.id)}
                >
                  {e.rsvp_count || 0} attendees
                </button>
              </td>
              <td style={styles.tableCell}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    style={styles.secondaryButton} 
                    onClick={() => { setCurrent(e); setShowModal(true); }}
                  >
                    ✏️ Edit
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
        <Modal title={current ? "Edit Event" : "New Event"} onClose={() => setShowModal(false)}>
          <EventForm data={current} onSave={handleSave} />
        </Modal>
      )}

      {showRsvpModal && (
        <Modal title="Event RSVPs" onClose={() => setShowRsvpModal(false)}>
          {rsvps.length > 0 ? (
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th style={styles.tableHeaderCell}>Name</th>
                  <th style={styles.tableHeaderCell}>Email</th>
                  <th style={styles.tableHeaderCell}>Status</th>
                  <th style={styles.tableHeaderCell}>Price</th>
                  <th style={styles.tableHeaderCell}>Payment</th>

                </tr>
              </thead>
              <tbody>
                {rsvps.map((r) => (
                  <tr key={r.id} style={styles.tableRow}>
                    <td style={styles.tableCell}>{r.names}</td>
                    <td style={styles.tableCell}>{r.email}</td>
                    <td style={styles.tableCell}>
                      <span style={{
                        ...styles.badge,
                        ...(r.rsvp ? styles.badgeSuccess : styles.badgeWarning)
                      }}>
                        {r.rsvp}
                      </span>
                    </td>
                    <td style={styles.tableCell}>{r.price}</td>
                    <td style={styles.tableCell}>{r.paymentIntentId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>👥</div>
              <p>No RSVPs yet for this event.</p>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function EventForm({ data, onSave }) {
  const [form, setForm] = useState(data || { 
    title: "", 
    description: "", 
    date: "", 
    time: "", 
    location: "", 
    event_type: "", 
    price: "",
    image_url: "",
    has_happened: true,
    published: true,
    speakers: [],
    agenda: []
  });

  const [activeTab, setActiveTab] = useState('basic');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  // Speaker Management
  const addSpeaker = () => {
    setForm({
      ...form,
      speakers: [
        ...form.speakers,
        {
          id: Date.now(),
          name: "",
          title: "",
          bio: "",
          image: ""
        }
      ]
    });
  };

  const updateSpeaker = (id, field, value) => {
    setForm({
      ...form,
      speakers: form.speakers.map(speaker =>
        speaker.id === id ? { ...speaker, [field]: value } : speaker
      )
    });
  };

  const removeSpeaker = (id) => {
    setForm({
      ...form,
      speakers: form.speakers.filter(speaker => speaker.id !== id)
    });
  };

  // Agenda Management
  const addAgendaItem = () => {
    setForm({
      ...form,
      agenda: [
        ...form.agenda,
        {
          id: Date.now(),
          title: "",
          speaker: "",
          time: ""
        }
      ]
    });
  };

  const updateAgendaItem = (id, field, value) => {
    setForm({
      ...form,
      agenda: form.agenda.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    });
  };

  const removeAgendaItem = (id) => {
    setForm({
      ...form,
      agenda: form.agenda.filter(item => item.id !== id)
    });
  };

  const moveAgendaItem = (id, direction) => {
    const currentIndex = form.agenda.findIndex(item => item.id === id);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === form.agenda.length - 1)
    ) {
      return;
    }

    const newAgenda = [...form.agenda];
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    [newAgenda[currentIndex], newAgenda[targetIndex]] = [newAgenda[targetIndex], newAgenda[currentIndex]];
    
    setForm({ ...form, agenda: newAgenda });
  };

  const TabButton = ({ tabKey, label, count = null }) => (
    <button
      type="button"
      onClick={() => setActiveTab(tabKey)}
      style={{
        ...eventstyles.tabButton,
        ...(activeTab === tabKey ? eventstyles.activeTab : {})
      }}
    >
      {label} {count !== null && <span style={eventstyles.tabCount}>({count})</span>}
    </button>
  );

  return (
    <div style={eventstyles.container}>
      {/* Tab Navigation */}
      <div style={eventstyles.tabContainer}>
        <TabButton tabKey="basic" label="Basic Info" />
        <TabButton tabKey="speakers" label="Speakers" count={form.speakers.length} />
        <TabButton tabKey="agenda" label="Agenda" count={form.agenda.length} />
      </div>

      {/* Basic Information Tab */}
      {activeTab === 'basic' && (
        <div style={eventstyles.tabContent}>
          <h3 style={eventstyles.sectionTitle}>📅 Event Information</h3>
          
          <div style={eventstyles.formGroup}>
            <label style={eventstyles.formLabel}>Event Title</label>
            <input 
              name="title" 
              value={form.title} 
              onChange={handleChange} 
              style={eventstyles.formInput}
              placeholder="Enter event title..."
              required 
            />
          </div>
          
          <div style={eventstyles.formGroup}>
            <label style={eventstyles.formLabel}>Description</label>
            <textarea 
              name="description" 
              value={form.description} 
              onChange={handleChange} 
              style={eventstyles.formTextarea}
              placeholder="Describe the event..."
              rows="4"
              required 
            />
          </div>

          <div style={eventstyles.formGroup}>
            <label style={eventstyles.formLabel}>Image URL</label>
            <input 
              name="image_url" 
              value={form.image_url} 
              onChange={handleChange} 
              style={eventstyles.formInput}
              placeholder="https://example.com/image.jpg"
              required 
            />
            {form.image_url && (
              <div style={eventstyles.imagePreview}>
                <img src={form.image_url} alt="Preview" style={eventstyles.previewImage} />
              </div>
            )}
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={eventstyles.formGroup}>
              <label style={eventstyles.formLabel}>Date</label>
              <input 
                name="date" 
                value={form.date} 
                onChange={handleChange} 
                style={eventstyles.formInput}
                required 
              />
            </div>
            
            <div style={eventstyles.formGroup}>
              <label style={eventstyles.formLabel}>Time</label>
              <input 
                name="time" 
                value={form.time} 
                onChange={handleChange} 
                style={eventstyles.formInput}
                required 
              />
            </div>
          </div>
          
          <div style={eventstyles.formGroup}>
            <label style={eventstyles.formLabel}>Location</label>
            <input 
              name="location" 
              value={form.location} 
              onChange={handleChange} 
              style={eventstyles.formInput}
              placeholder="Event location..."
              required 
            />
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={eventstyles.formGroup}>
              <label style={eventstyles.formLabel}>Event Type</label>
              <select 
                name="event_type" 
                value={form.event_type} 
                onChange={handleChange} 
                style={eventstyles.formInput}
                required 
              >
                <option value="">Select type...</option>
                <option value="summit">Summit</option>
                <option value="workshop">Workshop</option>
                <option value="conference">Conference</option>
                <option value="webinar">Webinar</option>
                <option value="networking">Networking</option>
                <option value="training">Training</option>
              </select>
            </div>
            
            <div style={eventstyles.formGroup}>
              <label style={eventstyles.formLabel}>Price</label>
              <input 
                name="price" 
                value={form.price} 
                onChange={handleChange} 
                style={eventstyles.formInput}
                placeholder="e.g., Free, £50..."
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={eventstyles.formGroup}>
              <label style={eventstyles.checkboxLabel}>
                <input 
                  name="published"
                  type="checkbox"
                  checked={form.published} 
                  onChange={handleChange} 
                  style={eventstyles.checkbox}
                />
                Published
              </label>
            </div>
            
            <div style={eventstyles.formGroup}>
              <label style={eventstyles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="has_happened" 
                  checked={form.has_happened} 
                  onChange={handleChange} 
                  style={eventstyles.checkbox}
                />
                Event has occurred
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Speakers Tab */}
      {activeTab === 'speakers' && (
        <div style={eventstyles.tabContent}>
          <div style={eventstyles.sectionHeader}>
            <h3 style={eventstyles.sectionTitle}>🎤 Event Speakers</h3>
            <button 
              type="button" 
              onClick={addSpeaker}
              style={eventstyles.addButton}
            >
              ➕ Add Speaker
            </button>
          </div>

          {form.speakers.length === 0 ? (
            <div style={eventstyles.emptyState}>
              <p>No speakers added yet. Click "Add Speaker" to get started.</p>
            </div>
          ) : (
            <div style={eventstyles.speakersList}>
              {form.speakers.map((speaker, index) => (
                <div key={speaker.id} style={eventstyles.speakerCard}>
                  <div style={eventstyles.cardHeader}>
                    <h4 style={eventstyles.cardTitle}>Speaker {index + 1}</h4>
                    <button 
                      type="button" 
                      onClick={() => removeSpeaker(speaker.id)}
                      style={eventstyles.deleteButton}
                    >
                      🗑️
                    </button>
                  </div>

                  <div style={eventstyles.formRow}>
                    <div style={eventstyles.formGroup}>
                      <label style={eventstyles.formLabel}>Name</label>
                      <input 
                        value={speaker.name} 
                        onChange={(e) => updateSpeaker(speaker.id, 'name', e.target.value)}
                        style={eventstyles.formInput}
                        placeholder="Speaker's full name"
                        required 
                      />
                    </div>
                    <div style={eventstyles.formGroup}>
                      <label style={eventstyles.formLabel}>Title</label>
                      <input 
                        value={speaker.title} 
                        onChange={(e) => updateSpeaker(speaker.id, 'title', e.target.value)}
                        style={eventstyles.formInput}
                        placeholder="Job title or position"
                        required 
                      />
                    </div>
                  </div>

                  <div style={eventstyles.formGroup}>
                    <label style={eventstyles.formLabel}>Bio</label>
                    <textarea 
                      value={speaker.bio} 
                      onChange={(e) => updateSpeaker(speaker.id, 'bio', e.target.value)}
                      style={eventstyles.formTextarea}
                      placeholder="Brief biography..."
                      rows="3"
                      required 
                    />
                  </div>

                  <div style={eventstyles.formGroup}>
                    <label style={eventstyles.formLabel}>Image URL</label>
                    <input 
                      value={speaker.image} 
                      onChange={(e) => updateSpeaker(speaker.id, 'image', e.target.value)}
                      style={eventstyles.formInput}
                      placeholder="https://example.com/speaker-photo.jpg"
                    />
                    {speaker.image && (
                      <div style={eventstyles.speakerImagePreview}>
                        <img src={speaker.image} alt={speaker.name} style={eventstyles.speakerImage} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Agenda Tab */}
      {activeTab === 'agenda' && (
        <div style={eventstyles.tabContent}>
          <div style={eventstyles.sectionHeader}>
            <h3 style={eventstyles.sectionTitle}>📋 Event Agenda</h3>
            <button 
              type="button" 
              onClick={addAgendaItem}
              style={eventstyles.addButton}
            >
              ➕ Add Agenda Item
            </button>
          </div>

          {form.agenda.length === 0 ? (
            <div style={eventstyles.emptyState}>
              <p>No agenda items added yet. Click "Add Agenda Item" to create the schedule.</p>
            </div>
          ) : (
            <div style={eventstyles.agendaList}>
              {form.agenda.map((item, index) => (
                <div key={item.id} style={eventstyles.agendaCard}>
                  <div style={eventstyles.cardHeader}>
                    <div style={eventstyles.agendaOrder}>
                      <span style={eventstyles.orderNumber}>{index + 1}</span>
                      <div style={eventstyles.moveButtons}>
                        <button 
                          type="button" 
                          onClick={() => moveAgendaItem(item.id, 'up')}
                          disabled={index === 0}
                          style={eventstyles.moveButton}
                        >
                          ⬆️
                        </button>
                        <button 
                          type="button" 
                          onClick={() => moveAgendaItem(item.id, 'down')}
                          disabled={index === form.agenda.length - 1}
                          style={eventstyles.moveButton}
                        >
                          ⬇️
                        </button>
                      </div>
                    </div>
                    <button 
                      type="button" 
                      onClick={() => removeAgendaItem(item.id)}
                      style={eventstyles.deleteButton}
                    >
                      🗑️
                    </button>
                  </div>

                    <div style={eventstyles.formGroup}>
                      <label style={eventstyles.formLabel}>Title/Topic</label>
                      <input 
                        value={item.title} 
                        onChange={(e) => updateAgendaItem(item.id, 'title', e.target.value)}
                        style={eventstyles.formInput}
                        placeholder="Session title or topic"
                        required 
                      />
                    </div>

                    <div style={eventstyles.formGroup}>
                      <label style={eventstyles.formLabel}>Speaker</label>
                      <input 
                        type="speaker"
                        value={item.speaker} 
                        onChange={(e) => updateAgendaItem(item.id, 'speaker', e.target.value)}
                        style={eventstyles.formInput}
                      />
                    </div>

                  <div style={eventstyles.formGroup}>
                    <label style={eventstyles.formLabel}>Time</label>
                    <input 
                      value={item.time} 
                      onChange={(e) => updateAgendaItem(item.id, 'time', e.target.value)}
                      style={eventstyles.formInput}
                      placeholder="9:00 AM"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Action Buttons */}
      <div style={eventstyles.actionButtons}>
        <button 
          type="button" 
          style={eventstyles.secondaryButton}
          onClick={() => window.location.reload()}
        >
          Cancel
        </button>
        <button 
          type="button" 
          style={eventstyles.primaryButton}
          onClick={() => onSave(form)}
        >
          💾 Save Event
        </button>
      </div>
    </div>
  );
}

// Articles component
function Articles({ token, logout, showNotification }) {
  const [articles, setArticles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [current, setCurrent] = useState(null);

  // Mock data
  const fetchArticles = async () => {
    const response = await apiCall("/api/admin/articles", "GET", null, token, logout);
    if (response.ok) {
        const data = await response.json()
        setArticles(data || []);
    } else {
        showNotification('unable to fetch articles', 'error');
    }
  };

  useEffect(() => { fetchArticles(); }, [token]);

  const handleSave = async (article) => {
    let response = undefined;
    if (article.id) {
      response = await apiCall(`/api/admin/articles/${article.id}`, "PUT", article, token, logout);
    } else {
      response = await apiCall("/api/admin/articles", "POST", article, token, logout);
    }
    if (response.ok) {
        setShowModal(false);
        fetchArticles();
        showNotification(article.id ? 'updated article': 'created article', 'success');
    } else {
        showNotification('error updating articles', 'error');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={styles.cardTitle}>
          <span>📝</span>
          Articles Management
        </h2>
        <button 
          style={styles.primaryButton} 
          onClick={() => { setCurrent(null); setShowModal(true); }}
        >
          <span>➕</span>
          Add Article
        </button>
      </div>

      <table style={styles.table}>
        <thead style={styles.tableHeader}>
          <tr>
            <th style={styles.tableHeaderCell}>Article</th>
            <th style={styles.tableHeaderCell}>Author</th>
            <th style={styles.tableHeaderCell}>Category</th>
            <th style={styles.tableHeaderCell}>Status</th>
            <th style={styles.tableHeaderCell}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {articles.map((a) => (
            <tr key={a.id} style={styles.tableRow}>
              <td style={styles.tableCell}>
                <div style={{ fontWeight: '600' }}>{a.title}</div>
                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                  Created: {a.created_at}
                </div>
              </td>
              <td style={styles.tableCell}>{a.author}</td>
              <td style={styles.tableCell}>
                <span style={{...styles.badge, ...styles.badgeInfo}}>
                  {a.category}
                </span>
              </td>
              <td style={styles.tableCell}>
                <span style={{
                  ...styles.badge,
                  ...(a.published ? styles.badgeSuccess : styles.badgeWarning)
                }}>
                  {a.published ? "Published" : "Draft"}
                </span>
              </td>
              <td style={styles.tableCell}>
                <button 
                  style={styles.secondaryButton} 
                  onClick={() => { setCurrent(a); setShowModal(true); }}
                >
                  ✏️ Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
        <Modal title={current ? "Edit Article" : "New Article"} onClose={() => setShowModal(false)}>
          <ArticleForm data={current} onSave={handleSave} />
        </Modal>
      )}
    </div>
  );
}

function ArticleForm({ data, onSave }) {
  const [form, setForm] = useState(data || { 
    title: "",
    excerpt: "",
    content: "", 
    author: "", 
    category: "", 
    image_url: "",
    published: false 
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  return (
    <div>
      <div style={styles.formGroup}>
        <label style={styles.formLabel}>Title</label>
        <input 
          name="title" 
          value={form.title} 
          onChange={handleChange} 
          style={styles.formInput}
          placeholder="Article title..."
          required 
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.formLabel}>Excerpt</label>
        <input 
          name="excerpt" 
          value={form.excerpt} 
          onChange={handleChange} 
          style={styles.formInput}
          placeholder="Excerpt..."
          required 
        />
      </div>

     <div style={styles.formGroup}>
        <label style={styles.formLabel}>Image URL</label>
        <input 
          name="image_url" 
          value={form.image_url} 
          onChange={handleChange} 
          style={styles.formInput}
          placeholder="Image preview..."
          required 
        />
      </div>
      
      <div style={styles.formGroup}>
        <label style={styles.formLabel}>Content</label>
        <textarea 
          name="content" 
          value={form.content} 
          onChange={handleChange} 
          style={styles.formTextarea}
          placeholder="Write your article content here..."
          required 
        />
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Author</label>
          <input 
            name="author" 
            value={form.author} 
            onChange={handleChange} 
            style={styles.formInput}
            placeholder="Author name..."
            required 
          />
        </div>
        
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Category</label>
          <input 
            name="category" 
            value={form.category} 
            onChange={handleChange} 
            style={styles.formInput}
            placeholder="e.g., Business, Trade..."
            required 
          />
        </div>
      </div>
      
      <div style={styles.formGroup}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input 
            type="checkbox" 
            name="published" 
            checked={form.published} 
            onChange={handleChange} 
          /> 
          <span style={styles.formLabel}>Published</span>
        </label>
      </div>
      
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '2rem' }}>
        <button type="button" style={styles.secondaryButton}>Cancel</button>
        <button 
          type="button" 
          style={styles.primaryButton}
          onClick={() => onSave(form)}
        >
          💾 Save Article
        </button>
      </div>
    </div>
  );
}

// Members component
function Members({ token, logout, showNotification }) {
  const [members, setMembers] = useState([]);
  const [current, setCurrent] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    (async () => {
      const response = await apiCall("/api/admin/members", "GET", null, token, logout);
      if (response.ok) {
        const data = await response.json()
        setMembers(data || []);
      } else {
        showNotification('error loading members', 'errors');
      }

    })();
  }, [token]);

  return (
    <div>
      <h2 style={styles.cardTitle}>
        <span>👥</span>
        Members Management
      </h2>
      
      <table style={styles.table}>
        <thead style={styles.tableHeader}>
          <tr>
            <th style={styles.tableHeaderCell}>Member</th>
            <th style={styles.tableHeaderCell}>Company</th>
            <th style={styles.tableHeaderCell}>Industry</th>
            <th style={styles.tableHeaderCell}>Membership Tier</th>
            <th style={styles.tableHeaderCell}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.id} style={styles.tableRow}>
              <td style={styles.tableCell}>
                <div style={{ fontWeight: '600' }}>
                  {m.first_name} {m.last_name}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                  {m.email}
                </div>
              </td>
              <td style={styles.tableCell}>{m.company || "N/A"}</td>
              <td style={styles.tableCell}>
                <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                  {m.industry || "N/A"}
                </span>
              </td>                
             <td style={styles.tableCell}>
                <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                  {m.membershiptier || "N/A"}
                </span>
              </td>
              <td onClick={() => { setCurrent(m); setShowModal(true); }}
                style={styles.tableCell}>
                    <button style={styles.secondaryButton}>
                    👁️ View
                    </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
        <Modal title={"View Member"} onClose={() => setShowModal(false)}>
          <MemberViewForm data={current} />
        </Modal>
      )}

    </div>
  );
}

function MemberViewForm({ data, onSave }) {
  const [form, setForm] = useState(data);

  return (
    <div>
      {/* Personal Information */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>First Name </label>
          <input 
            name="first_name" 
            value={form.first_name} 
            style={styles.formInput}
            placeholder="Enter first name..."
            disabled={true} 
          />
        </div>
        
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Last Name </label>
          <input 
            name="last_name" 
            value={form.last_name} 
            style={styles.formInput}
            placeholder="Enter last name..."
            disabled={true} 
          />
        </div>
      </div>

      {/* Contact Information */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Email *</label>
          <input 
            type="email"
            name="email" 
            value={form.email} 
            style={styles.formInput}
            placeholder="contact@example.com"
            disabled={true}  
          />
        </div>
        
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Company</label>
          <input 
            name="company" 
            value={form.company} 
            style={styles.formInput}
            placeholder="Company name (optional)"
            disabled={true} 
          />
        </div>
      </div>

      {/* Subject and Status */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>industry </label>
          <input 
            name="industry" 
            value={form.industry || "N/A"} 
            style={styles.formInput}
            disabled={true} 
          />
        </div>
        
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>interests </label>
          <input 
            name="interests" 
            value={form.interests || "N/A"} 
            style={styles.formInput}
            disabled={true} 
          />
        </div>
      </div>

      {/* Message */}
      <div style={styles.formGroup}>
        <label style={styles.formLabel}>phone </label>
        <textarea 
          name="phone" 
          value={form.phone} 
          style={styles.formInput}
          disabled={true} 
        />
      </div>
      
      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '2rem' }}>
        <button type="button" style={styles.secondaryButton}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// Contacts component  
function Contacts({ token, logout, showNotification }) {
  const [contacts, setContacts] = useState([]);
  const [current, setCurrent] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchContacts = async () => {
    const response = await apiCall("/api/admin/contacts", "GET", null, token, logout);
    if (response.ok) {
        const data = await response.json()
        setContacts(data || []);
    } else {
        showNotification('error loading contacts', 'errors');
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [token]);

  const handleSave = async (event) => {
    const response = await apiCall(`/api/admin/contacts/${event.id}/status`, "PUT", event, token, logout);
    if (response.ok) {
        setShowModal(false);
        fetchContacts();
        showNotification('updated contact', 'success');
    } else {
        showNotification('error updating contact', 'error');
    }
  };

  return (
    <div>
      <h2 style={styles.cardTitle}>
        <span>💬</span>
        Contact Inquiries
      </h2>
      
      <table style={styles.table}>
        <thead style={styles.tableHeader}>
          <tr>
            <th style={styles.tableHeaderCell}>Contact</th>
            <th style={styles.tableHeaderCell}>Subject</th>
            <th style={styles.tableHeaderCell}>Status</th>
            <th style={styles.tableHeaderCell}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {contacts.map((c) => (
            <tr key={c.id} style={styles.tableRow}>
              <td style={styles.tableCell}>
                <div style={{ fontWeight: '600' }}>
                  {c.first_name} {c.last_name}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                  {c.email}
                </div>
              </td>
              <td style={styles.tableCell}>{c.subject}</td>
              <td style={styles.tableCell}>
                <span style={{
                  ...styles.badge,
                  ...(c.status === 'new' ? styles.badgeWarning : styles.badgeSuccess)
                }}>
                  {c.status}
                </span>
              </td>
              <td 
                onClick={() => { setCurrent(c); setShowModal(true); }}
              style={styles.tableCell}>
                <button style={styles.secondaryButton}>
                  💬 Reply
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
        <Modal title={"View Contact"} onClose={() => setShowModal(false)}>
          <ContactForm data={current} onSave={handleSave} />
        </Modal>
      )}
    </div>
  );
}

function ContactForm({ data, onSave }) {
  const [form, setForm] = useState(data || { 
    first_name: "",
    last_name: "",
    email: "",
    company: "",
    subject: "",
    message: "",
    status: "new"
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  return (
    <div>
      {/* Personal Information */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>First Name *</label>
          <input 
            name="first_name" 
            value={form.first_name} 
            onChange={handleChange} 
            style={styles.formInput}
            placeholder="Enter first name..."
            disabled={true} 
          />
        </div>
        
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Last Name *</label>
          <input 
            name="last_name" 
            value={form.last_name} 
            onChange={handleChange} 
            style={styles.formInput}
            placeholder="Enter last name..."
            disabled={true} 
          />
        </div>
      </div>

      {/* Contact Information */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Email *</label>
          <input 
            type="email"
            name="email" 
            value={form.email} 
            onChange={handleChange} 
            style={styles.formInput}
            placeholder="contact@example.com"
            disabled={true}  
          />
        </div>
        
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Company</label>
          <input 
            name="company" 
            value={form.company} 
            onChange={handleChange} 
            style={styles.formInput}
            placeholder="Company name (optional)"
            disabled={true} 
          />
        </div>
      </div>

      {/* Subject and Status */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Subject *</label>
          <input 
            name="subject" 
            value={form.subject} 
            onChange={handleChange} 
            style={styles.formInput}
            placeholder="What is this inquiry about?"
            disabled={true} 
          />
        </div>
        
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Status</label>
          <select 
            name="status" 
            value={form.status} 
            onChange={handleChange} 
            style={styles.formSelect}
          >
            <option value="new">🆕 New</option>
            <option value="replied">⏳ In Progress</option>
            <option value="closed">🔒 Closed</option>
          </select>
        </div>
      </div>

      {/* Message */}
      <div style={styles.formGroup}>
        <label style={styles.formLabel}>Message *</label>
        <textarea 
          name="message" 
          value={form.message} 
          onChange={handleChange} 
          style={styles.formTextarea}
          placeholder="Enter the inquiry message..."
          rows={5}
          disabled={true} 
        />
      </div>
      
      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '2rem' }}>
        <button type="button" style={styles.secondaryButton}>
          Cancel
        </button>
        <button 
          type="button" 
          style={styles.primaryButton}
          onClick={() => onSave(form)}
        >
          💾 Save Contact
        </button>
      </div>
    </div>
  );
}

// Main AdminPanel component
export default function AdminPanel() {
  const [token, setToken] = useState(localStorage.getItem("admin_token")); // Start with null for demo
  const [page, setPage] = useState("dashboard");
  const [notifications, setNotifications] = useState([]);

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
  };

    const showNotification = (message, type = 'success') => {
        const id = Date.now();
        const notification = { id, message, type };
        setNotifications(prev => [...prev, notification]);
        
        // Auto remove after 5 seconds
        setTimeout(() => {
            setNotifications(prev => prev.filter(n => n.id !== id));
        }, 5000);
    };

    const removeNotification = (id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    };

  if (!token) return <LoginForm onLogin={setToken} />;

  return (
    <div style={styles.body}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      <div style={styles.container}>
        <header style={styles.header}>
          <div style={styles.headerOverlay}></div>
          <div style={styles.headerContent}>
            <h1 style={styles.headerTitle}>Saudi British Network</h1>
            <p style={styles.headerSubtitle}>Admin Panel - Manage your network content and members</p>
          </div>
        </header>
        <NotificationContainer removeNotification={removeNotification} notifications={notifications} />

        <Navbar setPage={setPage} logout={logout} currentPage={page} />

        <div style={styles.card}>
          {page === "dashboard" && <Dashboard token={token} logout={logout} showNotification={showNotification} />}
          {page === "articles" && <Articles token={token} logout={logout} showNotification={showNotification} />}
          {page === "events" && <Events token={token} logout={logout} showNotification={showNotification} />}
          {page === "members" && <Members token={token} logout={logout} showNotification={showNotification} />}
          {page === "contacts" && <Contacts token={token} logout={logout} showNotification={showNotification} />}
        </div>
      </div>
    </div>
  );
}